import React, { useState, useEffect, useRef } from "react"
import DatePicker from "react-datepicker"
import axios from "axios"
import { sub } from "date-fns"
import { useToasts } from "react-toast-notifications"
import { FastField, useFormikContext } from "formik"
import { navigate } from "gatsby"

import IntlTelInput from "react-intl-tel-input"
import "react-intl-tel-input/dist/main.css"

import "react-datepicker/dist/react-datepicker.css"
import {
  getBusinessNature,
  getCountries,
  getState,
  getIdentityTypes,
} from "../../../../Helpers"

import FormError from "../../../../formError"

const AdditionalInfo = () => {
  const { values, errors, touched, handleChange, setFieldValue } =
    useFormikContext()
  const { addToast } = useToasts()
  const inputFileRef = useRef(null)
  const signatureFileRef = useRef(null)
  const passportFileRef = useRef(null)
  const [st, setSt] = useState([])
  const [fileWarrningText, setFileWarningText] = useState("")
  const [countries, setCountries] = useState([])
  const [identityTypes, setIdentityTypes] = useState([])

  const getStates = () => {
    getState()
      .then((res) => {
        setSt(res.data.data)
      })
      .catch((err) => {
        console.log(err)
      })
  }

  const getAllCountries = async () => {
    return getCountries()
      .then((res) => {
        setCountries(res.data)
      })
      .catch((err) => {
        console.log(err)
      })
  }

  const getIdentities = async () => {
    return getIdentityTypes()
      .then((res) => {
        setIdentityTypes(res.data)
      })
      .catch((err) => {
        console.log(err)
      })
  }

  useEffect(() => {
    getStates()
    getAllCountries()
    getIdentities()
  }, [])

  const [loading, setLoading] = useState(false)

  const uploadImage = (file_data, file_location) => {
    setLoading(true)

    const myHeaders = new Headers()
    myHeaders.append("Accept", "application/json")
    myHeaders.append("token", `${localStorage.getItem("mobile_token")}`)

    const data = new FormData()
    data.append("file", file_data, file_data.name)

    axios
      .post(
        `${process.env.GATSBY_SPARKLE_LOCAL_BASE_URL}/api/v1/business/web/document-upload`,
        data, // this should be the FormData object directly
        {
          headers: {
            Accept: "application/json",
            token: localStorage.getItem("mobile_token"),
          },
          redirect: "follow",
        }
      )
      .then((res) => {
        setFieldValue(file_location, res.data.data.file)
        setLoading(false)
      })
      .catch((err) => {
        setLoading(false)
        addToast(err.response.data.message, {
          appearance: "error",
          placement: "top-center",
          autoDismiss: true,
        })
        if (err.response.data.message === "Token expired") {
          navigate("/business-support/get-started/")
        }
        clearFileInputs()
      })
  }
  const clearFileInputs = () => {
    if (inputFileRef.current) {
      inputFileRef.current.value = ""
    }
    if (signatureFileRef.current) {
      signatureFileRef.current.value = ""
    }
    if (passportFileRef.current) {
      passportFileRef.current.value = ""
    }
  }
  const setFile = (file, location) => {
    const maxFileSize = 5 * 1024 * 1024 // 5MB in bytes

    if (file.size > maxFileSize) {
      setFileWarningText("File size should not exceed 5MB.")
      addToast("File size should not exceed 5MB", {
        appearance: "error",
        placement: "top-center",
        autoDismiss: true,
      })
      clearFileInputs()
      setTimeout(() => setFileWarningText(""), 2000)

      return
    }
    uploadImage(file, location)
  }

  return (
    <>
      <div className="form-row justify-between">
        <div className="form-group col-md-5">
          <label htmlFor="firstname"> First Name</label>
          <FastField
            type="text"
            className="form-control"
            id="firstname"
            name="firstname"
            autoComplete="given-name"
          />

          <FormError touched={touched.firstname} message={errors.firstname} />
        </div>

        <div className="form-group col-md-5">
          <label htmlFor="lastname"> Last Name</label>
          <FastField
            type="text"
            className="form-control"
            id="lastname"
            name="lastname"
            autoComplete="family-name"
          />

          <FormError touched={touched.lastname} message={errors.lastname} />
        </div>
      </div>

      <div className="form-row justify-between">
        <div className="form-group col-md-5">
          <label htmlFor="proprietor_email">Email Address</label>
          <input
            type="text"
            className="form-control"
            id="proprietor_email"
            name="proprietor_email"
            onChange={handleChange}
            value={values.proprietor_email}
          />

          <FormError
            touched={touched.proprietor_email}
            message={errors.proprietor_email}
          />
        </div>

        <div className="form-group col-md-5">
          <label htmlFor="proprietor_phone">Phone Number</label>

          <IntlTelInput
            inputClassName="form-control"
            defaultCountry="ng"
            id="proprietor_phone"
            fieldName="proprietor_phone"
            autoComplete="tel"
            onPhoneNumberChange={(fullnumber, newNumber) => {
              setFieldValue("proprietor_phone", newNumber)
            }}
            value={values.proprietor_phone}
            placeholder="XXXX XXXX XXX"
          />

          <FormError
            touched={touched.proprietor_phone}
            message={errors.proprietor_phone}
          />
        </div>
      </div>

      <div className="form-row justify-between">
        <div className="form-group col-md-5">
          <label htmlFor="gender">Gender</label>
          <FastField
            type="text"
            className="form-control custom-select"
            id="gender"
            name="gender"
            as="select"
          >
            <option value="" label="Select Gender">
              Select Gender
            </option>
            <option value="Male">Male</option>
            <option value="Female">Female</option>
          </FastField>

          <FormError touched={touched.gender} message={errors.gender} />
        </div>

        <div className="form-group col-md-5">
          <label htmlFor="nationality">Nationality</label>
          <FastField
            className="form-control custom-select"
            as="select"
            id="nationality"
            name="nationality"
          >
            <option value="">Select Country</option>
            {countries.map((country) => (
              <option key={country.value} value={country.value}>
                {country.display}
              </option>
            ))}
          </FastField>

          <FormError
            touched={touched.nationality}
            message={errors.nationality}
          />
        </div>
      </div>

      <div className="form-row justify-between">
        <div className="form-group col-md-5">
          <label htmlFor="proprietor_state">State</label>
          <FastField
            type="text"
            className="form-control custom-select"
            id="proprietor_state"
            name="proprietor_state"
            as="select"
          >
            <option value="" label="Select State">
              Select State
            </option>
            {st.map((data) => (
              <option key={data.id} value={data.name}>
                {data.name}
              </option>
            ))}
          </FastField>

          <FormError
            touched={touched.proprietor_state}
            message={errors.proprietor_state}
          />
        </div>

        <div className="form-group col-md-5">
          <label htmlFor="proprietor_city">City</label>
          <FastField
            type="text"
            className="form-control"
            id="proprietor_city"
            name="proprietor_city"
          />

          <FormError
            touched={touched.proprietor_city}
            message={errors.proprietor_city}
          />
        </div>
      </div>

      <div className="form-row">
        <div className="form-group col-md-12">
          <label htmlFor="proprietor_address">Home Address</label>
          <textarea
            type="text"
            className="form-control"
            id="proprietor_address"
            name="proprietor_address"
            onChange={handleChange}
            value={values.proprietor_address}
            rows="3"
          ></textarea>

          <FormError
            touched={touched.proprietor_address}
            message={errors.proprietor_address}
          />
        </div>
      </div>

      <div className="form-row justify-between">
        <div className="form-group col-md-5">
          <label htmlFor="dob">Date of birth</label>

          <DatePicker
            selected={values.dob}
            onChange={(date) => setFieldValue("dob", date)}
            minDate={sub(new Date(), {
              years: 500,
            })}
            maxDate={sub(new Date(), {
              years: 18,
            })}
            placeholderText="Must be 18 years or older"
            peekNextMonth
            showMonthDropdown
            showYearDropdown
            dropdownMode="select"
            className="form-control"
            type="date"
            id="dob"
            name="dob"
          />

          <FormError touched={touched.dob} message={errors.dob} />
        </div>

        <div className="form-group col-md-5">
          <label htmlFor="occupation">Occupation</label>
          <FastField
            type="text"
            className="form-control"
            id="occupation"
            name="occupation"
          />

          <FormError touched={touched.occupation} message={errors.occupation} />
        </div>
      </div>

      <div className="form-row justify-between">
        <div className="form-group col-md-5">
          <label htmlFor="identity_type">Identity Type</label>
          <FastField
            className="form-control custom-select"
            as="select"
            id="identity_type"
            name="identity_type"
          >
            <option value="">Select Type</option>
            {identityTypes.map((identity) => (
              <option key={identity.value} value={identity.value}>
                {identity.display}
              </option>
            ))}
          </FastField>

          <FormError
            touched={touched.identity_type}
            message={errors.identity_type}
          />
        </div>

        <div className="form-group col-md-5">
          <label htmlFor="identity_number">Identity Number</label>
          <FastField
            type="text"
            className="form-control"
            id="identity_number"
            name="identity_number"
          />

          <FormError
            touched={touched.identity_number}
            message={errors.identity_number}
          />
        </div>
      </div>

      <div
        className={
          loading
            ? "progress_state form-row justify-content-center"
            : "form-row justify-content-center"
        }
      >
        <div className="form-group col-md-12">
          <label htmlFor="identity">Upload Identity Document</label>
          <input
            type="file"
            className="form-control border-0"
            id="identity"
            name="identity"
            accept="application/pdf"
            title="Accept pdf only"
            ref={inputFileRef}
            onChange={(e) => setFile(e.target.files[0], "identity")}
          />

          <label htmlFor="identity" className="font-weight-light text-muted">
            pdf file only
          </label>

          <FormError touched={touched.identity} message={errors.identity} />
        </div>
      </div>

      <div
        className={
          loading
            ? "progress_state form-row justify-content-center"
            : "form-row justify-content-center"
        }
      >
        <div className="form-group col-md-12">
          <label htmlFor="signature">Upload signature</label>
          <input
            type="file"
            className="form-control border-0"
            id="signature"
            name="signature"
            title="Accept png, jpeg, jpg"
            ref={signatureFileRef}
            onChange={(e) => setFile(e.target.files[0], "signature")}
          />

          <label htmlFor="signature" className="font-weight-light text-muted">
            png, jpeg, jpg files
          </label>

          <FormError touched={touched.signature} message={errors.signature} />
        </div>
      </div>

      <div
        className={
          loading
            ? "progress_state form-row justify-content-center"
            : "form-row justify-content-center"
        }
      >
        <div className="form-group col-md-12">
          <label htmlFor="passport">Upload passport photo</label>
          <input
            type="file"
            className="form-control border-0"
            id="passport"
            name="passport"
            accept="image/png, image/jpeg"
            title="Accept png,jpeg file only"
            ref={passportFileRef}
            onChange={(e) => setFile(e.target.files[0], "passport")}
          />

          <label htmlFor="passport" className="font-weight-light text-muted">
            image file only
          </label>

          <FormError touched={touched.passport} message={errors.passport} />
        </div>
        {fileWarrningText && (
          <p className="sparkle--font__14 sparkle-bright-orange sparkle--weight__600">
            {fileWarrningText}
          </p>
        )}
        <p>{loading && "Processing"}</p>
      </div>
    </>
  )
}

export default AdditionalInfo
